import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import { ReactComponent as Crown } from '../../../assets/img/crown.svg';
import { sendTrainingRequest } from '../../../redux/actions/Training';
import { hidePopupWindow } from '../../../redux/actions/Popups';
import {
  getTrainingLoading,
  trainingTeams,
  getTrainingOfferLoading,
  getTrainingOfferResponse,
} from '../../../redux/selector/Training';

import {
  StyledPopUp,
  StyledMultisectionPopup,
  StyledLoader,
  StyledCross,
  StyledSubmitButton,
} from '../styled';
import {
  StyledLeagueContainer,
  StyledTeam,
  StyledLeagueHeader,
  StyledContainer,
  StyledHeader,
  StyledTeamsContainer,
  StyledCrest,
  StyledCrestFallback,
  StyledTeamName,
} from '../FriendlyPopup/styled';
import { isAuth } from '../../../redux/selector/User';

import { goToDiscordLogin } from '../../../service/functions';
import InviteServerSelect from '../../InviteServerSelect';
import useInviteServerFilter from '../../../hooks/useInviteServerFilter';

const teamCrest = (team, crestByTeamId) =>
  team.symbol || (team._id != null ? crestByTeamId[String(team._id)] : '');

const TrainingPopup = ({ popupName, leaguesList }) => {
  const [activeTeamSelectId, setActiveTeamId] = React.useState(null);
  const dispatch = useDispatch();
  const trainingData = useSelector(trainingTeams);
  const isTrainingLoading = useSelector(getTrainingLoading);
  const isTrainingOfferLoading = useSelector(getTrainingOfferLoading);
  const trainingOfferResponse = useSelector(getTrainingOfferResponse);
  const isAuthenticated = useSelector(isAuth);
  const {
    universeOptions,
    selectedUniverseName,
    setSelectedUniverseName,
    leagues,
    isHomeServer,
    remoteStatus,
    getTeamsForLeague,
    crestByTeamId,
  } = useInviteServerFilter({
    inviteTeams: trainingData,
    fallbackLeagues: leaguesList,
  });

  const sendTraining = (teamId) => {
    setActiveTeamId(teamId);
    dispatch(sendTrainingRequest(teamId));
  };

  const hidePopup = () => {
    dispatch(hidePopupWindow(popupName));
  };

  const buttonText = (teamData, offerResponse) => {
    if (teamData.bot) {
      return 'Select';
    }
    if (offerResponse === teamData._id) {
      return 'Sent';
    }
    return 'Select';
  };

  const showRemoteEmpty = !isHomeServer && remoteStatus === 'empty';
  const showRemoteLoader = !isHomeServer && remoteStatus === 'loading';

  const renderLeagues = (leagueItems) =>
    leagueItems.map((league) => {
      const teamsForLeague = getTeamsForLeague(league);
      if (!isHomeServer && isEmpty(teamsForLeague)) {
        return null;
      }
      if (isHomeServer && isEmpty(teamsForLeague)) {
        return null;
      }
      return (
        <StyledLeagueContainer key={league._id || league.name}>
          <StyledLeagueHeader>{league.name}</StyledLeagueHeader>
          <div>
            {teamsForLeague.map((team) => {
              const crest = teamCrest(team, crestByTeamId);
              return (
                <StyledTeam key={team._id}>
                  {crest ? (
                    <StyledCrest src={crest} alt="" />
                  ) : (
                    <StyledCrestFallback aria-hidden>
                      {(team.name || '?').charAt(0)}
                    </StyledCrestFallback>
                  )}
                  <StyledTeamName>
                    {team.name}
                    {team.premium && <Crown />}
                  </StyledTeamName>
                  <StyledSubmitButton
                    primary
                    isGrey={team.training}
                    disabled={
                      isTrainingOfferLoading && activeTeamSelectId === team._id
                    }
                    isCompleted={trainingOfferResponse === team._id}
                    onClick={() => {
                      if (!isAuthenticated) {
                        goToDiscordLogin();
                      } else {
                        sendTraining(team._id);
                      }
                    }}
                  >{`${buttonText(team, trainingOfferResponse)}`}</StyledSubmitButton>
                </StyledTeam>
              );
            })}
          </div>
        </StyledLeagueContainer>
      );
    });

  return (
    <StyledPopUp>
      <StyledMultisectionPopup>
        {isTrainingLoading ? (
          <StyledLoader type="Bars" color="var(--tol-header-accent)" height={100} width={100} />
        ) : (
          <StyledContainer>
            <StyledHeader>
              Practice - Select a partner
              <StyledCross onClick={() => hidePopup()} />
            </StyledHeader>
            <InviteServerSelect
              embedded
              value={selectedUniverseName}
              options={universeOptions}
              onChange={setSelectedUniverseName}
            />
            {showRemoteLoader ? (
              <StyledLoader type="Bars" color="var(--tol-header-accent)" height={80} width={80} />
            ) : showRemoteEmpty ? (
              <StyledTeamsContainer>
                {(leagues.slice(0, 3) || []).map((league) => (
                  <StyledLeagueContainer key={league._id || league.name}>
                    <StyledLeagueHeader>{league.name}</StyledLeagueHeader>
                    <div>
                      <StyledTeam>
                        <StyledCrestFallback aria-hidden>—</StyledCrestFallback>
                        <StyledTeamName>No teams available</StyledTeamName>
                      </StyledTeam>
                    </div>
                  </StyledLeagueContainer>
                ))}
              </StyledTeamsContainer>
            ) : (
              <StyledTeamsContainer>{renderLeagues(leagues)}</StyledTeamsContainer>
            )}
          </StyledContainer>
        )}
      </StyledMultisectionPopup>
    </StyledPopUp>
  );
};

export default TrainingPopup;
